import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf, faDownload, faFilter } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const RESUMO_VAZIO = {
  totalProdutos: 0,
  unidadesEmEstoque: 0,
  valorTotal: 0,
  produtosBaixoEstoque: 0,
  produtosZerados: 0,
  saldosNegativos: []
};

const CORES_DA_FAIXA = {
  zerado: 'bg-red-100 text-red-800',
  baixo: 'bg-orange-100 text-orange-800',
  normal: 'bg-blue-100 text-blue-800',
  alto: 'bg-green-100 text-green-800'
};

const mensagemDeErro = (error) => {
  const dados = error.response?.data;
  const porCampo = (dados?.validationErrors || []).map(e => e.msg).filter(Boolean);
  if (porCampo.length > 0) return porCampo.join(' ');
  if (!error.response) return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
  return dados?.message
    ? `${dados.message}. Tente de novo em instantes.`
    : 'Não foi possível carregar o relatório. Tente de novo em instantes.';
};

const EstoqueRelatorio = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [truncado, setTruncado] = useState(false);
  const [faixas, setFaixas] = useState([]);
  const ultimaRequisicao = useRef(0);
  const [filtros, setFiltros] = useState({
    status: 'Ativo',
    categoria: '',
    tipoProduto: '',
    busca: ''
  });
  const [resumo, setResumo] = useState(RESUMO_VAZIO);
  const faixaBaixo = faixas.find(f => f.categoria === 'baixo');

  // Helper para formatar moeda brasileira
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  // Helper para formatar número grande
  const formatarNumero = (numero) => {
    return new Intl.NumberFormat('pt-BR').format(numero || 0);
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async (f = filtros) => {
    // Só a requisição mais recente pode tocar na tela: uma busca lenta que
    // responde depois de outra mais nova não pode sobrescrever o resultado.
    const requisicao = ++ultimaRequisicao.current;
    const ehAMaisRecente = () => requisicao === ultimaRequisicao.current;
    try {
      setLoading(true);
      setErro('');

      const params = {};
      if (f.categoria) params.categoria = f.categoria;
      if (f.status) params.status = f.status;
      if (f.tipoProduto.trim()) params.tipoProduto = f.tipoProduto.trim();
      if (f.busca.trim()) params.busca = f.busca.trim();

      const response = await api.get('/estoque-dashboard/produtos', { params });
      if (!ehAMaisRecente()) return;

      setDados(response.data.data || []);
      setResumo({ ...RESUMO_VAZIO, ...response.data.resumo });
      setTruncado(Boolean(response.data.truncado));
      setFaixas(response.data.faixas || []);
    } catch (error) {
      if (!ehAMaisRecente()) return;
      // Sem isso a tela continuava mostrando (e exportando) o resultado anterior
      // como se fosse do filtro novo.
      setDados([]);
      setResumo(RESUMO_VAZIO);
      setTruncado(false);
      setErro(mensagemDeErro(error));
    } finally {
      if (ehAMaisRecente()) setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const novos = { ...filtros, [e.target.name]: e.target.value };
    setFiltros(novos);
    // Lista selecionada aplica na hora: a tela não pode mostrar um filtro e
    // listar (e exportar) outro. Campo de texto só aplica com Enter ou botão.
    if (e.target.tagName === 'SELECT') {
      carregarDados(novos);
      return;
    }
    // Esvaziar a busca (apagando, Esc ou o "x" do campo) volta à lista sem a busca,
    // para a tela não mostrar um campo vazio com resultados filtrados.
    if (e.target.name === 'busca' && !novos.busca.trim() && filtros.busca.trim()) {
      carregarDados(novos);
    }
  };

  const aplicarFiltrosComEnter = (e) => {
    if (e.key === 'Enter') aplicarFiltros();
  };

  const aplicarFiltros = () => {
    carregarDados();
  };

  const exportarExcel = () => {
    const dadosExport = dados.map(item => ({
      'Código': item.idProduto,
      'Nome': item.produtoNome,
      'Tipo Produto': item.tipoProdutoNome || '-',
      'Disponível': item.estoqueDisponivel || 0,
      'Reservado': item.quantidadeReservada || 0,
      'Total': item.estoqueFisico || 0,
      'Valor Unitário': item.valor || 0,
      'Valor Total': item.valorEstoque || 0,
      'Situação': item.categoriaRotulo || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(dadosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Relatório de Estoque');
    
    // Ajustar largura das colunas
    const colWidths = [
      { wch: 8 },  // Código
      { wch: 40 }, // Nome
      { wch: 20 }, // Tipo Produto
      { wch: 18 }, // Estoque Disponível
      { wch: 18 }, // Estoque Reservado
      { wch: 15 }, // Estoque Total
      { wch: 15 }, // Valor Unitário
      { wch: 15 }, // Valor Total
      { wch: 10 }  // Situação
    ];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `Relatorio_Estoque_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportarPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Título
    doc.setFontSize(18);
    doc.text('Relatório de Estoque', 14, 15);
    
    // Data
    doc.setFontSize(10);
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 14, 22);
    
    // Resumo
    doc.setFontSize(12);
    doc.text('Resumo:', 14, 32);
    doc.setFontSize(10);
    doc.text(`Total de Produtos: ${resumo.totalProdutos}`, 14, 38);
    doc.text(`Unidades em estoque (físico): ${formatarNumero(resumo.unidadesEmEstoque)}`, 14, 43);
    doc.text(`Valor do estoque (a preço de venda): ${formatarMoeda(resumo.valorTotal)}`, 14, 48);
    doc.text(`Estoque baixo: ${resumo.produtosBaixoEstoque} - Zerados: ${resumo.produtosZerados}`, 14, 53);
    
    // Tabela
    const tableData = dados.map(item => [
      item.idProduto,
      item.produtoNome,
      item.tipoProdutoNome || '-',
      formatarNumero(item.estoqueDisponivel || 0),
      formatarNumero(item.quantidadeReservada || 0),
      formatarNumero(item.estoqueFisico || 0),
      formatarMoeda(item.valor || 0),
      formatarMoeda(item.valorEstoque || 0),
      item.categoriaRotulo || '-'
    ]);

    autoTable(doc, {
      startY: 60,
      head: [['Cód', 'Nome', 'Tipo', 'Disponível', 'Reservado', 'Total', 'Valor Unit.', 'Valor Total', 'Situação']],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [34, 197, 94] },
      margin: { left: 14, right: 14 }
    });

    doc.save(`Relatorio_Estoque_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden p-4">
      <div className="bg-white rounded shadow p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Relatório de Estoque</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportarExcel}
              disabled={loading || dados.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              Exportar Excel
            </button>
            <button
              onClick={exportarPDF}
              disabled={loading || dados.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faFilePdf} />
              Exportar PDF
            </button>
          </div>
        </div>

        {erro && (
          <div role="alert" className="mb-6 p-4 rounded border border-red-200 bg-red-50 text-red-800">
            {erro}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded min-w-0">
            <p className="text-sm text-gray-600">Total de Produtos</p>
            <p className="text-2xl font-bold text-blue-600 break-words">{formatarNumero(resumo.totalProdutos)}</p>
          </div>
          <div className="bg-green-50 p-4 rounded min-w-0">
            <p className="text-sm text-gray-600">Unidades em estoque (físico)</p>
            <p className="text-2xl font-bold text-green-600 break-words">{formatarNumero(resumo.unidadesEmEstoque)}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded min-w-0">
            <p className="text-sm text-gray-600">Valor do estoque (a preço de venda)</p>
            <p className="text-2xl font-bold text-purple-600 break-words">{formatarMoeda(resumo.valorTotal)}</p>
          </div>
          <div className="bg-orange-50 p-4 rounded min-w-0">
            <p className="text-sm text-gray-600">
              Estoque baixo{faixaBaixo ? ` (${faixaBaixo.minimo} a ${faixaBaixo.maximo} disponíveis)` : ''}
            </p>
            <p className="text-2xl font-bold text-orange-600">{formatarNumero(resumo.produtosBaixoEstoque)}</p>
          </div>
          <div className="bg-red-50 p-4 rounded min-w-0">
            <p className="text-sm text-gray-600">Zerados (nada disponível)</p>
            <p className="text-2xl font-bold text-red-600">{formatarNumero(resumo.produtosZerados)}</p>
          </div>
        </div>

        {resumo.saldosNegativos.length > 0 && (
          <div className="mb-6 p-4 rounded border border-yellow-300 bg-yellow-50 text-yellow-900">
            <p className="font-semibold">Saldos negativos a corrigir</p>
            <p className="text-sm mb-2">
              Saiu mais do que entrou nestes produtos. Eles não entram no valor nem nas unidades em estoque
              até que a entrada que falta seja lançada.
            </p>
            <ul className="text-sm list-disc pl-5">
              {resumo.saldosNegativos.map(p => (
                <li key={p.idProduto} className="break-words">
                  {p.produtoNome} (cód. {p.idProduto}): {formatarNumero(p.estoqueFisico)} no físico
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-gray-50 p-4 rounded mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faFilter} className="text-gray-600" />
            <h3 className="font-semibold text-gray-700">Filtros</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-[minmax(0,2fr)_auto_auto_minmax(0,1fr)_auto] gap-3">
            <input
              type="search"
              name="busca"
              value={filtros.busca}
              onChange={handleFiltroChange}
              onKeyDown={aplicarFiltrosComEnter}
              placeholder="Buscar por nome do produto"
              aria-label="Buscar por nome do produto"
              maxLength={100}
              className="w-full min-w-0 md:col-span-2 2xl:col-span-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <select
              name="categoria"
              value={filtros.categoria}
              onChange={handleFiltroChange}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="">Todas as faixas de estoque</option>
              {faixas.map(f => (
                <option key={f.categoria} value={f.categoria}>{f.descricao}</option>
              ))}
            </select>
            <select
              name="status"
              value={filtros.status}
              onChange={handleFiltroChange}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="Ativo">Produtos ativos</option>
              <option value="Inativo">Produtos inativos</option>
              <option value="Todos">Todos os produtos</option>
            </select>
            <input
              type="text"
              name="tipoProduto"
              value={filtros.tipoProduto}
              onChange={handleFiltroChange}
              onKeyDown={aplicarFiltrosComEnter}
              placeholder="Tipo de Produto"
              aria-label="Tipo de produto"
              maxLength={100}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <button
              onClick={aplicarFiltros}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Aplicar Filtros
            </button>
          </div>
        </div>

        {truncado && (
          <p className="mb-3 text-sm text-gray-700">
            Mostrando {formatarNumero(dados.length)} de {formatarNumero(resumo.totalProdutos)} produtos.
            Os totais acima consideram todos; use os filtros para ver os demais na lista.
          </p>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="mt-2 text-gray-600">Carregando dados...</p>
            </div>
          ) : dados.length === 0 ? (
            !erro && (
              <div className="text-center py-8 text-gray-500">
                Nenhum produto encontrado
              </div>
            )
          ) : (
            <table className="min-w-full bg-white border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 border text-left">Código</th>
                  <th className="px-4 py-2 border text-left">Nome</th>
                  <th className="px-4 py-2 border text-left">Tipo</th>
                  <th className="px-4 py-2 border text-right">Disponível</th>
                  <th className="px-4 py-2 border text-right">Reservado</th>
                  <th className="px-4 py-2 border text-right">Total</th>
                  <th className="px-4 py-2 border text-right">Valor Unit.</th>
                  <th className="px-4 py-2 border text-right">Valor Total</th>
                  <th className="px-4 py-2 border text-center">Situação</th>
                </tr>
              </thead>
              <tbody>
                {dados.map((item) => (
                  <tr key={item.idProduto} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{item.idProduto}</td>
                    <td className="px-4 py-2 border">{item.produtoNome || '-'}</td>
                    <td className="px-4 py-2 border">{item.tipoProdutoNome || '-'}</td>
                    <td className="px-4 py-2 border text-right">{formatarNumero(item.estoqueDisponivel)}</td>
                    <td className="px-4 py-2 border text-right">{formatarNumero(item.quantidadeReservada)}</td>
                    <td className="px-4 py-2 border text-right">
                      {formatarNumero(item.estoqueFisico)}
                    </td>
                    <td className="px-4 py-2 border text-right">
                      {formatarMoeda(item.valor)}
                    </td>
                    <td className="px-4 py-2 border text-right">
                      {formatarMoeda(item.valorEstoque)}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${CORES_DA_FAIXA[item.categoriaEstoque] || ''}`}>
                        {item.categoriaRotulo}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstoqueRelatorio;
